<style lang="scss" scoped>
/* 横向脑图节点：卡片在左、子链竖排在右，连接线用 CSS 绘制 */
.lc-node {
    position: relative;
    display: flex;
    align-items: center;

    /* 到母线的横向短线 */
    &::before {
        content: '';
        position: absolute;
        left: -18px;
        width: 18px;
        top: 50%;
        border-top: 1.5px solid #dcdfe6;
    }

    /* 纵向母线段：首/尾子节点各收一半，避免线头露出分支上下界 */
    &::after {
        content: '';
        position: absolute;
        left: -18px;
        top: 0;
        bottom: 0;
        border-left: 1.5px solid #dcdfe6;
    }

    &:first-child::after {
        top: 50%;
    }

    &:last-child::after {
        bottom: 50%;
    }

    &:only-child::after {
        display: none;
    }
}

.lc-node--root {
    &::before,
    &::after {
        display: none;
    }
}

.lc-card {
    flex: none;
    width: max-content;
    max-width: 300px;
    padding: 7px 10px;
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    cursor: pointer;
    user-select: text;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    &.is-selected {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    }

    &.is-muted {
        background: #fafbfc;
        border-style: dashed;
    }
}

.lc-card__head {
    display: flex;
    align-items: center;
    gap: 6px;
}

.lc-tag {
    flex: none;
    font-size: 11px;
    line-height: 16px;
    color: #fff;
    border-radius: 3px;
    padding: 0 6px;
}

.lc-valid {
    flex: none;
    font-size: 11px;
    line-height: 16px;
    border-radius: 3px;
    padding: 0 6px;

    &.is-ok {
        color: #67c23a;
        background: #f0f9eb;
    }

    &.is-warn {
        color: #e6a23c;
        background: #fdf6ec;
    }

    &.is-end {
        color: #909399;
        background: #f4f4f5;
    }
}

.lc-cnt {
    flex: none;
    font-size: 11px;
    color: #606266;
    background: #f0f2f5;
    border-radius: 8px;
    padding: 0 6px;
    line-height: 16px;
}

/* 展开按钮：与卡片点击区分（click.stop），懒加载下一层关联 */
.lc-expander {
    flex: none;
    width: 18px;
    height: 18px;
    line-height: 16px;
    text-align: center;
    border: 1px solid #dcdfe6;
    border-radius: 50%;
    color: #606266;
    background: #fff;
    font-size: 12px;

    &:hover {
        color: #409eff;
        border-color: #409eff;
    }
}

/* 编号主文本：真 DOM 文本，可拖选复制 */
.lc-key {
    font-size: 13px;
    font-weight: 700;
    color: #303133;
    line-height: 20px;
    margin-top: 2px;
    word-break: break-all;
    cursor: text;
}

.lc-sub {
    font-size: 11px;
    color: #909399;
    line-height: 16px;
    margin-top: 2px;
    word-break: break-all;
}

.lc-children {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-left: 30px;
    margin-left: 14px;
    position: relative;

    /* 父卡片 → 母线的横向连接（覆盖 margin 与母线间的空档） */
    &::before {
        content: '';
        position: absolute;
        left: -14px;
        width: 26px;
        top: 50%;
        border-top: 1.5px solid #dcdfe6;
    }
}

.lc-none {
    font-size: 11px;
    color: #c0c4cc;
    background: #fafbfc;
    border: 1px dashed #e4e7ed;
    border-radius: 4px;
    padding: 2px 8px;
}
</style>

<template>
    <div class="lc-node" :class="{ 'lc-node--root': isRoot }">
        <div class="lc-card" :class="cardClass" :style="cardStyle" @click="onClick">
            <div class="lc-card__head">
                <span class="lc-tag" :style="{ background: tagColor }">{{ tagText }}</span>
                <span v-if="node.validTag" class="lc-valid" :class="validClass">{{ node.validTag }}</span>
                <span v-if="node.entityType != null" class="lc-cnt">日志 {{ node.logCount }}</span>
                <span v-if="node.expandable" class="lc-expander" title="展开/收起下一层关联" @click.stop="$emit('expand', node)">
                    <i :class="node.loading ? 'el-icon-loading' : node.expanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'" />
                </span>
            </div>
            <div class="lc-key" :title="keyText">{{ keyText }}</div>
            <div v-if="subText" class="lc-sub">{{ subText }}</div>
        </div>
        <div v-if="node.expanded" class="lc-children">
            <log-chain-node
                v-for="child in node.children"
                :key="child.path"
                :node="child"
                :selected-path="selectedPath"
                v-on="$listeners" />
            <div v-if="!node.loading && !node.children.length" class="lc-none">{{ node.allHidden ? '重复节点已省略' : '无关联数据' }}</div>
        </div>
    </div>
</template>

<script>
    // 全链路脑图递归节点：卡片 = 类型标签 + 可选中复制的编号 + 日志数 + 辅助标注（+保单有效性、懒加载展开按钮）；
    // 点击卡片上抛 select（右侧查日志），点展开按钮上抛 expand（懒加载下一层关联）
    const nodeTypeNames = { vehicle: '车辆同步', control: '中控', chassis: '车架', insurance: '车架保险', policy: '保单' }
    const tagNames = { ...nodeTypeNames, query: '查询', missing: '缺口' }
    export const nodeColors = { vehicle: '#409EFF', control: '#67C23A', chassis: '#E6A23C', insurance: '#F56C6C', policy: '#909399', query: '#606266', missing: '#C0C4CC' }
    const nodeTints = { vehicle: '#ecf5ff', control: '#f0f9eb', chassis: '#fdf6ec', insurance: '#fef0f0', policy: '#f4f4f5', query: '#f4f4f5', missing: '#fafafa' }
    const nodeBorders = { vehicle: '#b3d8ff', control: '#c2e7b0', chassis: '#f5dab1', insurance: '#fbc4c4', policy: '#d3d4d6', query: '#d3d4d6', missing: '#e4e7ed' }
    const validClasses = { 有效: 'is-ok', 未生效: 'is-warn', 已结束: 'is-end' }

    export default {
        name: 'LogChainNode',
        props: {
            node: { type: Object, required: true },
            isRoot: { type: Boolean, default: false },
            selectedPath: { type: String, default: null }
        },
        computed: {
            tagText() { return tagNames[this.node.nodeType] || '节点' },
            tagColor() { return nodeColors[this.node.nodeType] || '#909399' },
            validClass() { return validClasses[this.node.validTag] || 'is-end' },
            // 卡片主文本：查询包装卡显示"车辆查询 编号"、保险汇总卡显示绑定条数、占位卡显示缺口文案，其余显示业务键
            keyText() {
                if (this.node.nodeType === 'query') return this.node.label
                if (this.node.nodeType === 'insurance') return this.node.subLabel || this.node.businessKey
                if (this.node.nodeType === 'missing') return this.node.label
                return this.node.businessKey || this.node.label
            },
            subText() { return this.node.nodeType === 'insurance' ? '' : (this.node.subLabel || '') },
            cardClass() {
                return {
                    'is-selected': this.node.path === this.selectedPath,
                    'is-muted': this.node.nodeType === 'missing' || this.node.nodeType === 'query'
                }
            },
            cardStyle() {
                const type = this.node.nodeType
                const color = nodeColors[type] || '#409EFF'
                if (this.node.path === this.selectedPath) {
                    return { borderColor: color, borderWidth: '2px', background: nodeTints[type] || '#f0f2f5' }
                }
                return { borderColor: nodeBorders[type] || '#e4e7ed' }
            }
        },
        methods: {
            onClick() { this.$emit('select', this.node) }
        }
    }
</script>
